import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

// Fetches a protected profile photo (Bearer-authenticated) into an object URL.
// Falls back to initials when absent or unavailable. Sizes via `className`.
//
// Memory/identity safety:
//  * the previously created object URL is ALWAYS revoked before a new one is set
//    and on unmount (no leaks on replacement);
//  * a stale image is cleared immediately when `photoUrl` becomes null (photo
//    removed) or the signed-in identity changes (`identityKey`), so one user
//    never momentarily sees another's photo after a switch or sign-out.
export function AuthedAvatar({ photoUrl, name, identityKey, className = "h-10 w-10", textClass = "text-sm" }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const urlRef = useRef(null);

  const revoke = () => {
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  };

  useEffect(() => {
    let active = true;
    // Clear any prior image immediately (prevents stale/cross-identity flashes).
    revoke();
    setSrc(null);
    setFailed(false);
    if (photoUrl) {
      api.profilePhotoObjectUrl(photoUrl)
        .then((url) => {
          if (!active) { URL.revokeObjectURL(url); return; }
          urlRef.current = url;
          setSrc(url);
        })
        .catch(() => { if (active) setFailed(true); });
    }
    return () => { active = false; revoke(); };
  }, [photoUrl, identityKey]);

  const initials = (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "A";

  if (photoUrl && src && !failed) {
    return (
      <img
        src={src}
        alt={name || "Profile photo"}
        className={`${className} rounded-full object-cover bg-[hsl(var(--pc-surface2))]`}
      />
    );
  }
  return (
    <div
      aria-label={name || "Profile"}
      className={`${className} ${textClass} rounded-full grid place-items-center font-semibold bg-[hsl(var(--pc-gold)/0.18)] text-[hsl(var(--pc-gold))] border border-[hsl(var(--pc-gold)/0.3)]`}
    >
      {initials}
    </div>
  );
}
